import React, { useState, useCallback, useEffect } from "react";
import { Portal, Button } from "@/components";
import { useAppDispatch } from "@/redux/store";
import { fetchDeleteGame, fetchGetCreatedGames, fetchGetGamesList, fetchGetAppliedGames, setGames } from "@/redux/games";
import { fetchDeleteTournament, fetchGetCreatedTournaments, fetchGetAvailableTournaments, fetchGetMyApplicationsTournaments } from "@/redux/tournaments";
import { closeModal, openModal } from "react-url-modal";
import styles from "./DeleteGameAndTournamentModal.module.scss";

interface DeleteGameAndTournamentModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  gameId?: number;
  tournamentId?: number;
  date?: string;
  onSuccess?: () => void;
}

export const DeleteGameAndTournamentModal: React.FC<DeleteGameAndTournamentModalProps> = ({
  isOpen,
  setIsOpen,
  gameId,
  tournamentId,
  date,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const isTournament = !!tournamentId;

  const isEventPast = () => {
    if (!date) return false;
    try {
      const eventDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate < today;
    } catch {
      return false;
    }
  };

  const handleClose = useCallback(() => {
    if (!isDeleting) {
      setIsOpen(false);
    }
  }, [isDeleting, setIsOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "visible";
    };
  }, [isOpen, handleClose]);

  const handleConfirm = async () => {
    try {
      setIsDeleting(true);

      if (isTournament && tournamentId) {
        await dispatch(fetchDeleteTournament(tournamentId)).unwrap();
        await Promise.all([
          dispatch(fetchGetCreatedTournaments()),
          dispatch(fetchGetAvailableTournaments()),
          dispatch(fetchGetMyApplicationsTournaments()),
        ]);
        setIsOpen(false);
        closeModal();
        if (onSuccess) onSuccess();
        openModal({ name: "successDeleteTournament" });
      } else if (gameId !== undefined) {
        await dispatch(fetchDeleteGame(gameId)).unwrap();
        const [allGames, applied] = await Promise.all([
          dispatch(fetchGetGamesList()).unwrap(),
          dispatch(fetchGetAppliedGames()).unwrap(),
        ]);
        await dispatch(fetchGetCreatedGames());
        const filtered = allGames.filter(
          (game) => !applied.some((appliedGame) => appliedGame.id === game.id)
        );
        dispatch(setGames(filtered));
        setIsOpen(false);
        closeModal();
        if (onSuccess) onSuccess();
        openModal({ name: "successDeleteGame" });
      }
    } catch (error) {
      console.error(`Error deleting ${isTournament ? "tournament" : "game"}:`, error);
    } finally {
      setIsDeleting(false);
    }
  };

  const isPast = isEventPast();
  const entityName = isTournament ? "турнир" : "игру";

  if (!isOpen) return null;

  return (
    <Portal>
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.card} onClick={(e) => e.stopPropagation()}>
          <span className={styles.title}>
            {isPast
              ? `Невозможно удалить ${entityName}, ${isTournament ? "который" : "которая"} уже ${isTournament ? "прошёл" : "прошла"}`
              : `Уверены, что хотите удалить ${entityName}?`}
          </span>
          {!isPast && (
            <Button
              onClickFunc={handleConfirm}
              disabled={isDeleting}
              newStyles={styles.deleteButton}
            >
              {isDeleting ? "Удаление..." : "Удалить"}
            </Button>
          )}
        </div>
      </div>
    </Portal>
  );
};
